import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { LogOut, Plus, Trash2, Video, ExternalLink, Mail, User, Phone, MessageSquare, Calendar, CheckCircle, ChevronUp, ChevronDown, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use relative URL for API calls
const API = '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    projectType: ''
  });

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    fetchVideos();
    fetchContactSubmissions();
  }, [navigate]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setLoading(false);
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/contact/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/videos`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video added successfully!');
      setFormData({ title: '', description: '', url: '', projectType: '' });
      setShowAddForm(false);
      fetchVideos();
    } catch (error) {
      toast.error('Failed to add video');
      console.error('Error adding video:', error);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video deleted successfully!');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
      console.error('Error deleting video:', error);
    }
  };

  const handleMarkAsRead = async (submissionId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/contact/submissions/${submissionId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Marked as read');
      fetchContactSubmissions();
    } catch (error) {
      toast.error('Failed to mark as read');
      console.error('Error marking as read:', error);
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/contact/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Submission deleted');
      fetchContactSubmissions();
    } catch (error) {
      toast.error('Failed to delete submission');
      console.error('Error deleting submission:', error);
    }
  };

  const handleMoveVideo = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= videos.length) return;
    
    // Create new array with swapped positions
    const newVideos = [...videos];
    [newVideos[index], newVideos[newIndex]] = [newVideos[newIndex], newVideos[index]];
    
    // Update local state immediately for responsiveness
    setVideos(newVideos);
    
    // Send reorder request to backend
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/videos/reorder`, 
        { videoIds: newVideos.map(v => v.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video order updated');
    } catch (error) {
      toast.error('Failed to reorder videos');
      console.error('Error reordering videos:', error);
      // Revert on error
      fetchVideos();
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      url: video.url,
      projectType: video.projectType || ''
    });
    setShowAddForm(true);
  };

  const handleUpdateVideo = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/videos/${editingVideo.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video updated successfully!');
      setShowAddForm(false);
      setEditingVideo(null);
      setFormData({ title: '', description: '', url: '', projectType: '' });
      fetchVideos();
    } catch (error) {
      toast.error('Failed to update video');
      console.error('Error updating video:', error);
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingVideo(null);
    setFormData({ title: '', description: '', url: '', projectType: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isYouTubeUrl = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = contactSubmissions.filter(s => !s.read).length;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-heading uppercase tracking-wide text-white">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Manage videos & contact submissions</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                View Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="videos" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 mb-8">
            <TabsTrigger value="videos" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
              <Video size={18} className="mr-2" />
              Videos ({videos.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-red-700 data-[state=active]:text-white relative">
              <Mail size={18} className="mr-2" />
              Contact Submissions ({contactSubmissions.length})
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5">
                  {unreadCount} new
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Videos Tab */}
          <TabsContent value="videos">
            {/* Add Video Button */}
            <div className="mb-8">
              <Button
                onClick={() => {
                  if (showAddForm && editingVideo) {
                    handleCancelEdit();
                  } else {
                    setShowAddForm(!showAddForm);
                    setEditingVideo(null);
                    setFormData({ title: '', description: '', url: '', projectType: '' });
                  }
                }}
                className="bg-red-700 hover:bg-red-800 transition-colors duration-200 text-white font-semibold"
              >
                <Plus size={18} className="mr-2" />
                {showAddForm ? 'Cancel' : 'Add New Video'}
              </Button>
            </div>

            {/* Add/Edit Video Form */}
            {showAddForm && (
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 mb-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {editingVideo ? 'Edit Video' : 'Add New Video'}
                </h2>
                <form onSubmit={editingVideo ? handleUpdateVideo : handleAddVideo} className="space-y-6">
                  <div>
                    <Label htmlFor="title" className="text-white mb-2 block">Video Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      required
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g., Sci-Fi Book Trailer"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="url" className="text-white mb-2 block">Video URL *</Label>
                    <Input
                      id="url"
                      name="url"
                      type="url"
                      required
                      value={formData.url}
                      onChange={handleChange}
                      placeholder="YouTube URL or direct video file URL"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Supports YouTube links or direct video URLs
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="projectType" className="text-white mb-2 block">Project Type</Label>
                    <Input
                      id="projectType"
                      name="projectType"
                      type="text"
                      value={formData.projectType}
                      onChange={handleChange}
                      placeholder="e.g., Book Trailer, Product Promo"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description" className="text-white mb-2 block">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Brief description of the project..."
                      rows={4}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500 resize-none"
                    />
                  </div>
                  <div className="flex gap-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-gradient-blue-gold hover:opacity-90 transition-opacity duration-200 text-white font-semibold"
                    >
                      {editingVideo ? 'Update Video' : 'Add Video'}
                    </Button>
                    {editingVideo && (
                      <Button
                        type="button"
                        onClick={handleCancelEdit}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </Card>
            )}

            {/* Videos List */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Videos ({videos.length})</h2>
              {loading ? (
                <p className="text-gray-400">Loading videos...</p>
              ) : videos.length === 0 ? (
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 p-12 text-center">
                  <Video className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400">No videos added yet. Click "Add New Video" to get started.</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {videos.map((video, index) => (
                    <Card
                      key={video.id}
                      className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300"
                    >
                      <div className="flex gap-4">
                        {/* Reorder Buttons */}
                        <div className="flex flex-col justify-center gap-1">
                          <Button
                            onClick={() => handleMoveVideo(index, 'up')}
                            variant="outline"
                            size="sm"
                            disabled={index === 0}
                            className="border-white/20 text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed p-1 h-8 w-8"
                            title="Move up"
                          >
                            <ChevronUp size={16} />
                          </Button>
                          <Button
                            onClick={() => handleMoveVideo(index, 'down')}
                            variant="outline"
                            size="sm"
                            disabled={index === videos.length - 1}
                            className="border-white/20 text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed p-1 h-8 w-8"
                            title="Move down"
                          >
                            <ChevronDown size={16} />
                          </Button>
                        </div>
                        
                        {/* Video Info */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-gray-500 text-sm">#{index + 1}</span>
                                <h3 className="text-xl font-bold text-white">{video.title}</h3>
                              </div>
                              {video.projectType && (
                                <span className="inline-block bg-blue-500/10 text-blue-400 text-xs font-semibold px-3 py-1 rounded-full mb-2">
                                  {video.projectType}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleEditVideo(video)}
                                variant="outline"
                                size="sm"
                                className="border-blue-500/20 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/50"
                                title="Edit video"
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                onClick={() => handleDeleteVideo(video.id)}
                                variant="outline"
                                size="sm"
                                className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                                title="Delete video"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                          {video.description && (
                            <p className="text-gray-400 text-sm mb-4">{video.description}</p>
                          )}
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">
                              {isYouTubeUrl(video.url) ? 'YouTube' : 'Direct Video'}
                            </span>
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-200"
                            >
                              View
                              <ExternalLink size={14} className="ml-1" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Contact Submissions Tab */}
          <TabsContent value="contacts">
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">
                Contact Submissions ({contactSubmissions.length})
                {unreadCount > 0 && (
                  <span className="ml-3 text-sm font-normal text-yellow-400">
                    {unreadCount} unread
                  </span>
                )}
              </h2>
              {contactSubmissions.length === 0 ? (
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 p-12 text-center">
                  <Mail className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400">No contact submissions yet.</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {contactSubmissions.map((submission) => (
                    <Card
                      key={submission.id}
                      className={`bg-white/5 backdrop-blur-sm border p-6 hover:bg-white/10 transition-all duration-300 ${
                        submission.read ? 'border-white/10' : 'border-blue-500/30'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                            <User className="text-blue-400" size={20} />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">{submission.name}</h3>
                            <p className="text-sm text-gray-400">
                              <Calendar size={12} className="inline mr-1" />
                              {formatDate(submission.submitted_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {!submission.read && (
                            <Button
                              onClick={() => handleMarkAsRead(submission.id)}
                              size="sm"
                              className="bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border-none"
                            >
                              <CheckCircle size={16} className="mr-1" />
                              Mark Read
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteSubmission(submission.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center text-sm text-gray-300">
                          <Mail size={14} className="mr-2 text-blue-400" />
                          <a
                            href={`mailto:${submission.email}`}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            {submission.email}
                          </a>
                        </div>
                        {submission.phone && (
                          <div className="flex items-center text-sm text-gray-300">
                            <Phone size={14} className="mr-2 text-blue-400" />
                            <a
                              href={`tel:${submission.phone}`}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              {submission.phone}
                            </a>
                          </div>
                        )}
                        <div className="flex items-start text-sm">
                          <span className="inline-block bg-yellow-500/10 text-yellow-400 text-xs font-semibold px-3 py-1 rounded-full">
                            {submission.projectType}
                          </span>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg mt-3">
                          <div className="flex items-start text-sm text-gray-300">
                            <MessageSquare size={14} className="mr-2 text-blue-400 mt-1 flex-shrink-0" />
                            <p className="leading-relaxed">{submission.message}</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
